import { BrandOptionFields } from '../../../models/course/ICourseContent';
import React, {
  CSSProperties,
  MouseEvent,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
} from 'react';
import InfoPopup from '../../atoms/info-popup/info-popup';
import styles from './course-modules-uk-au.module.scss';
import useWindowResize from '../../../hooks/use-window-resize.hook';
import { deviceDetect } from '../../../data/device-detect';
import { Breakpoint } from '../../../constants/breakpoints';
import { IModule } from '../../../models/course/ICourseData';
import Markdown from 'markdown-to-jsx';

type Props = {
  modules: IModule[];
  brandOptions: BrandOptionFields;
};

type ModuleProps = {
  module: IModule;
  brandOptions: BrandOptionFields;
};

const customStyle: CSSProperties = { bottom: '-30px', top: 'unset' }; // used for popup when it's in the bottom of the page

export function ModuleRow({ module, brandOptions }: ModuleProps) {
  const [popupContent, setPopupContent] = useState<string>();
  const elementRef = useRef(null);

  const [isBreakpointUp, setIsBreakpointUp] = useState(!deviceDetect.isMobile);

  useWindowResize((newWidth) => setIsBreakpointUp(newWidth >= Breakpoint.Medium), 100);

  const isBottomOfPage = useCallback(
    () => window.innerHeight - elementRef.current.getBoundingClientRect().bottom < 350,
    [],
  );

  const onClick = useCallback(
    (e: MouseEvent | KeyboardEvent) => {
      e.preventDefault();
      setPopupContent(module.notes);
    },
    [module.notes, setPopupContent],
  );

  return (
    <div className={`row mb-3 ${styles.row}`} ref={elementRef}>
      <div className='col-md-9 mb-2 mb-md-0'>
        <a
          tabIndex={0}
          className={`${styles['module-link']} ${popupContent ? styles.active : null}`}
          onClick={onClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation(); // Prevent the event from reaching the document-level listener
              onClick(e); // Open the popup
            }
          }}
        >
          {module.name}
        </a>
      </div>
      <div className='col-md-3 mb-3 mb-md-0'>
        <span className={styles[`credit-value`]}>
          {+module.credits
            ? `${+module.credits} ${brandOptions?.customizeCredits || 'Credits'}`
            : module.credits}
        </span>
      </div>
      {popupContent ? (
        <InfoPopup
          heading={'Course Description'}
          customStyle={isBottomOfPage() && isBreakpointUp ? customStyle : null}
          onClose={() => setPopupContent(null)}
          cssClass={styles.popup}
          hideOnTab
        >
          <Markdown>{popupContent}</Markdown>
        </InfoPopup>
      ) : null}
    </div>
  );
}

export default function CourseModulesUKAU({ modules, brandOptions }: Props) {
  return (
    <div className={`mb-3 ${styles.modules}`}>
      {modules.map((module, index) => (
        <ModuleRow key={`${module.code}-${index}`} brandOptions={brandOptions} module={module} />
      ))}
    </div>
  );
}
